import i18n from '@/utils/i18n';
import LockIcon from '@assets/icons/lock.svg';
import NoteIcon from '@assets/icons/note.svg';
import React, { useEffect, useMemo, useState } from 'react';
import SendNoteIcon from '@assets/icons/send-note.svg';
import { ActivityIndicator } from './activity-indicator';
import { Analytics } from '@/utils/analytics';
import { Checkbox, DEFAULT_CHECKBOX_SIZE } from './checkbox';
import { createStyles } from '@/contexts/theme-context';
import {
  LIST_ITEM_SEPARATOR_HEIGHT,
  SCREEN_PADDING,
  TYPOGRAPHY,
} from '@/styles';
import { ListItem, ListItemSeparator, ListOption } from './list-item';
import { SectionList, Text, View } from 'react-native';
import { Type } from './toast';
import { useCanWriteLineItemRequest } from '@/hooks/dynamic-config/use-can-write-line-item-request';
import { useMultiOrderGuideMutations } from '@/hooks/use-multi-order-guide-mutations';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';

const SHEET_HORIZONTAL_PADDING = 16;
const CHECKBOX_PADDING_RIGHT = 16;

interface Props {
  mode?: 'manage' | 'select';
  onSelectionChangeCallBack?: (addedToOrderGuide: boolean) => void;
  onSelectPartitionOverride?: (partitionUUID: string) => void;
  variantPackUUID: string;
}

export const OrderGuidePartitionList: React.FC<Props> = ({
  mode = 'manage',
  onSelectPartitionOverride,
  onSelectionChangeCallBack,
  variantPackUUID,
}) => {
  const { bottom } = useSafeAreaInsets();
  const { showToast } = useToasts();
  const [styles, theme] = useStyles();
  const { collections, variantPackPartitionMap, refetch } =
    useOrderGuideItemsContext();
  const { addToOrderGuide, removeFromOrderGuide } =
    useMultiOrderGuideMutations();
  const canWriteRequest = useCanWriteLineItemRequest();

  const [loadingPartitionSet, setLoadingPartitionSet] = useState<Set<string>>(
    new Set(),
  );
  const [addRemoveVariantPacksSet, setAddRemoveVariantPacksSet] = useState<
    Set<string>
  >(new Set());

  useEffect(() => {
    onSelectionChangeCallBack?.(
      Boolean(Array.from(addRemoveVariantPacksSet).length),
    );
  }, [addRemoveVariantPacksSet, onSelectionChangeCallBack]);

  const variantPackPartitions = useMemo(() => {
    return variantPackPartitionMap[variantPackUUID] || {};
  }, [variantPackPartitionMap, variantPackUUID]);

  const sections = useMemo(() => {
    const mapped = collections.map((collection) => {
      const data = collection.partitions.map((partition) => {
        return {
          title: partition.name,
          value: partition.uuid,
        };
      });

      // select mode only shows the partitions that are in the variantPackPartitions map
      if (mode === 'select') {
        return {
          data: data.filter(
            (partition) => variantPackPartitions[partition.value],
          ),
          isLocked: collection.isLocked,
          key: collection.name,
        };
      }

      return {
        data,
        isLocked: collection.isLocked,
        key: collection.name,
      };
    });

    // manage mode shows all sections, select mode only shows sections with data
    return mode === 'select'
      ? mapped.filter((section) => section.data.length > 0)
      : mapped;
  }, [collections, mode, variantPackPartitions]);

  const firstSectionKey = sections[0]?.key;

  const selectPartition = async (partitionUUID: string) => {
    if (loadingPartitionSet.has(partitionUUID)) {
      return;
    }

    setLoadingPartitionSet((previous) => new Set(previous.add(partitionUUID)));

    try {
      if (variantPackPartitions[partitionUUID]) {
        Analytics.track('order_guide_partition_selector.remove_button.press', {
          partitionUUID,
          variantPackUUID,
        });

        await removeFromOrderGuide(partitionUUID, variantPackUUID);
        setAddRemoveVariantPacksSet((previous) => {
          previous.delete(partitionUUID);
          return new Set(previous);
        });
      } else {
        Analytics.track('order_guide_partition_selector.add_button.press', {
          partitionUUID,
          variantPackUUID,
        });

        await addToOrderGuide(partitionUUID, variantPackUUID);
        setAddRemoveVariantPacksSet(
          (previous) => new Set(previous.add(partitionUUID)),
        );
      }
    } catch (e) {
      // Refetch the order guide from the server to get it back in sync
      refetch();

      showToast({
        content: i18n.t('toast.unsynced_order_guide_updated'),
        type: Type.PRIMARY,
      });
    }

    setLoadingPartitionSet((previous) => {
      previous.delete(partitionUUID);
      return new Set(previous);
    });
  };

  const handlePartitionSelect = onSelectPartitionOverride
    ? onSelectPartitionOverride
    : selectPartition;

  return (
    <SectionList<ListOption>
      contentContainerStyle={{
        paddingBottom: Math.max(bottom, SCREEN_PADDING),
      }}
      ItemSeparatorComponent={() => (
        <ListItemSeparator style={styles.partitionSeparator} />
      )}
      renderItem={({ item, section }) => (
        <ListItem
          item={item}
          leftComponentStyle={styles.listItemLeftComponent}
          listItemStyle={styles.listItem}
          listTitleStyle={
            section.isLocked ? styles.listItemTitleDisabled : null
          }
          onSelect={
            section.isLocked
              ? undefined
              : () => handlePartitionSelect(item.value as string)
          }
          renderLeftComponent={(option) => {
            const partitionUUID = option.value as string;
            if (mode === 'select') {
              return <View style={{ width: DEFAULT_CHECKBOX_SIZE }} />;
            }

            return (
              <View style={styles.checkboxWrapper}>
                {loadingPartitionSet.has(partitionUUID) ? (
                  <ActivityIndicator />
                ) : (
                  <Checkbox
                    disabled={section.isLocked}
                    isSelected={Boolean(variantPackPartitions[option.value])}
                    onSelect={() => handlePartitionSelect(partitionUUID)}
                    style={styles.checkbox}
                    trackingData={null}
                  />
                )}
              </View>
            );
          }}
          renderRightComponent={
            mode === 'select'
              ? (option) => {
                  const orderGuideItem =
                    variantPackPartitions[option.value as string];
                  const { memo, request } =
                    orderGuideItem?.order_guide_restaurant || {};
                  if (!memo && !request) {
                    return null;
                  }

                  return (
                    <View style={styles.iconContainer}>
                      {memo ? (
                        <NoteIcon
                          height={16}
                          stroke={theme.sk.color.icon.secondary}
                          width={16}
                        />
                      ) : null}
                      {request && canWriteRequest ? (
                        <SendNoteIcon
                          height={16}
                          stroke={theme.sk.color.icon.secondary}
                          width={16}
                        />
                      ) : null}
                    </View>
                  );
                }
              : undefined
          }
        />
      )}
      renderSectionHeader={({ section }) => (
        <View
          style={[
            styles.sectionHeader,
            section.key === firstSectionKey
              ? null
              : styles.sectionHeaderWithBorder,
          ]}
        >
          <Text
            style={[
              TYPOGRAPHY.listTitle,
              styles.sectionHeaderText,
              section.isLocked ? styles.sectionHeaderTextDisabled : null,
            ]}
          >
            {section.key}
          </Text>
          {section.isLocked ? (
            <LockIcon stroke={theme.sk.color.icon.tertiary} />
          ) : null}
        </View>
      )}
      sections={sections}
      stickySectionHeadersEnabled={false}
    />
  );
};

const useStyles = createStyles((theme) => ({
  checkbox: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  checkboxWrapper: {
    alignItems: 'center',
    minHeight: DEFAULT_CHECKBOX_SIZE,
    minWidth:
      DEFAULT_CHECKBOX_SIZE + SHEET_HORIZONTAL_PADDING + CHECKBOX_PADDING_RIGHT,
    paddingLeft: SHEET_HORIZONTAL_PADDING,
    paddingRight: CHECKBOX_PADDING_RIGHT,
  },
  iconContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  listItem: {
    paddingLeft: 0,
    paddingRight: SHEET_HORIZONTAL_PADDING,
    paddingVertical: 16,
  },
  listItemLeftComponent: {
    marginRight: 0,
  },
  listItemTitleDisabled: {
    color: theme.sk.color.text.disabled,
  },
  partitionSeparator: {
    marginHorizontal: SHEET_HORIZONTAL_PADDING,
  },
  sectionHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    marginHorizontal: SHEET_HORIZONTAL_PADDING,
    paddingTop: SCREEN_PADDING,
  },
  sectionHeaderText: {
    color: theme.colors.inactiveText,
    textTransform: 'uppercase',
  },
  sectionHeaderTextDisabled: {
    color: theme.sk.color.text.disabled,
  },
  sectionHeaderWithBorder: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: LIST_ITEM_SEPARATOR_HEIGHT,
  },
}));
